import React from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames/bind';

import ControlWithOverlay from '@ott/control-with-overlay';
import DropdownTrigger from './DropdownTrigger';

import s from './Dropdown.scss';

const cx = cls.bind(s);

const wrapperMainLocator = 'Dropdown';
const triggerMainLocator = 'DropdownTrigger';
const optionMainLocator = 'DropdownOption';

const renderDropdownOption = (item) => {
  const { name, specificLocators } = item;

  return <span data-locator={cls(optionMainLocator, specificLocators)}>{name}</span>;
};

class Dropdown extends ControlWithOverlay {
  static propTypes = {
    ...ControlWithOverlay.propTypes,
    initialActiveIdx: PropTypes.number,
    size: PropTypes.oneOf(['basic', 'medium', 'small']),
    label: PropTypes.node,
    // if no placeholder is passed - first children will be selected and placed as placeholder
    placeholder: PropTypes.node,
    className: PropTypes.string,
    placeholderDisposable: PropTypes.element,
    withArrow: PropTypes.bool,
    withPadding: PropTypes.bool,
    withBorder: PropTypes.bool,
    withBorderRadius: PropTypes.bool,
    // если необходимо, чтобы выпадающий overlay был привязан к какому-то другому элементу (например, в случае, если не
    // виден из-за overflow: hidden
    isStaticPosition: PropTypes.bool,
    childrenClassNames: PropTypes.shape({
      container: PropTypes.string,
      wrapper: PropTypes.string,
      overlay: PropTypes.string,
      label: PropTypes.string,
      arrowContainer: PropTypes.string,
    }),
    specificLocators: PropTypes.shape({
      wrapper: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    }),
  };

  static defaultProps = {
    ...ControlWithOverlay.defaultProps,
    size: 'basic',
    label: '',
    items: [],
    renderOption: renderDropdownOption,
    onOptionSelect: () => {},
    skipActive: true,
    withArrow: true,
    withPadding: true,
    withBorder: true,
    withBorderRadius: true,
    isStaticPosition: false,
    childrenClassNames: {
      container: '',
      wrapper: '',
      overlay: '',
      label: '',
      arrowContainer: '',
    },
    specificLocators: {
      wrapper: [],
      trigger: [],
    },
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.handleTab = this.handleTab.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);

    this.state = {
      ...this.state,
      selected: this.findInitial(props),
      //вызывалась ранее в конструкторе родителя
      //но там не был определен selected, чтобы корректно определить активную
      activeOption: super.findActiveOption(props),
    };
  }

  componentWillReceiveProps(nextProps) {
    const { initialActiveIdx, items } = this.props;
    super.componentWillReceiveProps(nextProps);

    if (
      (nextProps.initialActiveIdx !== initialActiveIdx && this._initial) ||
      items !== nextProps.items
    ) {
      this.setState({
        selected: this.findInitial(nextProps),
      });
    }
  }

  findInitial(props) {
    const { placeholder, placeholderDisposable, initialActiveIdx, items, initialValue } = props;

    let initialActive = initialValue;

    if (
      placeholderDisposable &&
      !initialActive &&
      !initialActiveIdx &&
      typeof initialActiveIdx === 'object'
    )
      return;

    if (initialActiveIdx != null) {
      initialActive = items[initialActiveIdx];
    }

    if (initialActive == null && !placeholder) {
      initialActive = items[0];
    }

    return initialActive;
  }

  getTriggerProps() {
    const { selected } = this.state;
    const {
      placeholderDisposable,
      size,
      withArrow,
      withPadding,
      withBorder,
      withBorderRadius,
      isError,
      specificLocators: { trigger: triggerLocator },
    } = this.props;

    let value;
    if (placeholderDisposable && !selected) {
      value = placeholderDisposable;
    } else {
      value = selected == null ? null : this.getTriggerValue(selected, 'trigger');
    }

    return {
      ...super.getTriggerProps(),
      size,
      value,
      label: selected == null ? this.props.placeholder : this.props.label,
      isError,
      withArrow,
      withPadding,
      withBorder,
      withBorderRadius,
      'data-error-placement': 'top',
      'data-locator': cls(triggerMainLocator, triggerLocator),
      ref: 'trigger',
    };
  }

  handleTab() {
    return true;
  }

  handleMouseDown(e) {
    if (this.state.isOpen) {
      if (this.state.optionFocused) {
        this._ignoreFocus = true;
      }
      this.hideSelect();
      setTimeout(() => (this._ignoreFocus = false), 0);
      return;
    }

    super.handleMouseDown(e);
  }

  renderTrigger() {
    const {
      childrenClassNames: { wrapper, label, arrowContainer },
      disabled,
    } = this.props;

    return (
      <DropdownTrigger
        childrenClassNames={{
          wrapper,
          label,
          arrowContainer,
        }}
        disabled={disabled}
        {...this.getTriggerProps()}
      />
    );
  }

  render() {
    const {
      align,
      tabIndex,
      isStaticPosition,
      childrenClassNames,
      specificLocators: { wrapper: wrapperLocator },
    } = this.props;

    const { isOpen } = this.state;

    return (
      <div
        className={cx('Dropdown', `Dropdown--align-${align}`, childrenClassNames.container, {
          'Dropdown--isOpen': isOpen,
          'Dropdown--staticPosition': isStaticPosition,
        })}
        tabIndex={tabIndex != null ? tabIndex : '0'}
        data-locator={cls(wrapperMainLocator, wrapperLocator)}
      >
        {this.renderTrigger()}
        {isOpen && (
          <div className={cx('overlay', childrenClassNames.overlay)}>{this.getOptions()}</div>
        )}
      </div>
    );
  }
}

export default Dropdown;
